import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import { UserDictionaryApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import NotificationContext from 'src/context/notification';
import {
  LearnablePronunciationData,
  LearnablePronunciationScore,
  PronunciationMode,
} from 'src/api/userDictionary/types';
import { convertAudioBufferToWav, convertBlobToAudioBuffer } from 'src/utils/audio';
import { useDevToolPermissions } from 'src/apps/Tools/DevTools/utils';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { DEV_TOOL_ENABLE_DEBUG_PANEL_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { ONE_SECOND } from 'src/constants/times.const';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useMicrophonePermissions } from '../hooks/useMicrophonePermissions';
import {
  MicPermissionsView,
  PronunciationLearnableView,
  PronunciationAnalysisView,
  ImmersivePronunciationView,
} from '../PronunciationViews';
import type { ExtraDebugInfo } from '../PronunciationDebugPanel';
import { AudioValidationResult, validateAudioBuffer } from '../utils/validateAudioBuffer';

const PronunciationDebugPanel = dynamic(
  () => import('../PronunciationDebugPanel').then(mod => mod.PronunciationDebugPanel),
  { ssr: false },
);

const MAX_RECORDING_DURATION_SEC = 10;

const PRONUNCIATION_RESULT_DISPLAY_DURATION = 3 * ONE_SECOND;

const msgs = defineMessages({
  errorNotification: { id: 'something_went_wrong' },
});

export const LearnablePronunciation: FC<{
  learnable: LearnablePronunciationData;
  progressIndicator?: React.ReactNode;
  continueButtons: React.ReactNode;
  onSkip?: () => void;
  onActivityEngagement?: () => void | Promise<void>;
  /** Called after the backend scores a recording as a pass. */
  onCorrectPronunciation?: () => void;
  extraDebugInfo?: ExtraDebugInfo;
  sessionId: string;
  /** Sent with each scoring request so attempts can be attributed to where they were made. */
  mode?: PronunciationMode;
  languagePairIdOverride?: number | null;
  /** Advances the session when Space or Enter is pressed after a correct pronunciation. */
  onContinue?: () => void;
  /** Renders a smaller, self-contained layout for embedding alongside a
   * screen that already shows the target/source text. */
  compact?: boolean;
  /** Hides the native-speaker video button, for embedding alongside a screen
   * that already shows that video. */
  hideVideoButton?: boolean;
  /** SPIKE: renders the full-bleed layout, keeping the speaker video mounted
   * through recording, analysis and the result instead of swapping views. */
  immersive?: boolean;
  /** SPIKE: the session's other words, so the immersive layout can build a
   * backdrop from their speakers for words that were recorded but never
   * filmed. */
  sessionLearnables?: LearnablePronunciationData[] | null;
}> = ({
  learnable,
  progressIndicator,
  continueButtons,
  onSkip,
  extraDebugInfo,
  onActivityEngagement,
  onCorrectPronunciation,
  sessionId,
  mode,
  languagePairIdOverride,
  onContinue,
  compact = false,
  hideVideoButton = false,
  immersive = false,
  sessionLearnables,
}) => {
  const intl = useIntl();
  const languagePairId = languagePairIdOverride ?? getLanguagePairId();
  const { setNotification } = useContext(NotificationContext);
  const areDevToolsAllowed = useDevToolPermissions();
  const debugPanelEnabled =
    areDevToolsAllowed && getLocalStorageEntry(DEV_TOOL_ENABLE_DEBUG_PANEL_STORAGE_KEY);

  const [isUploading, setIsUploading] = useState(false);
  const [analysisScore, setAnalysisScore] = useState<LearnablePronunciationScore | null>(null);
  const [lastAnalysisScore, setLastAnalysisScore] = useState<LearnablePronunciationScore | null>(
    null,
  );
  const resetAnalysisScore = useCallback(() => {
    setAnalysisScore(lastScore => {
      setLastAnalysisScore(lastScore);
      return null;
    });
  }, []);
  const [validationResult, setValidationResult] = useState<AudioValidationResult>('valid');
  const [retryCount, setRetryCount] = useState(0);

  const { micPermissionState, handleGiveMicPermissions } = useMicrophonePermissions();

  useEffect(() => {
    setIsUploading(false);
    resetAnalysisScore();
    setValidationResult('valid');
    setRetryCount(0);
  }, [learnable, resetAnalysisScore]);

  const setErrorNotification = () => {
    setNotification({
      text: intl.formatMessage(msgs.errorNotification),
      status: 'error',
      snackBar: true,
    });
  };

  const processRecording = async (audioBlob: Blob) => {
    /* istanbul ignore if: should not happen */
    if (!learnable || !languagePairId) {
      setIsUploading(false);
      return;
    }

    let wavAudioBlob: Blob;
    try {
      const audioBuffer = await convertBlobToAudioBuffer(audioBlob);
      const validationResult = validateAudioBuffer(audioBuffer);

      if (validationResult !== 'valid') {
        setValidationResult(validationResult);
        setIsUploading(false);
        return;
      }

      wavAudioBlob = new Blob([convertAudioBufferToWav(audioBuffer)], { type: 'audio/wav' });
    } catch (err) /* istanbul ignore next: untested branch of code, please test */ {
      console.error('Could not convert audio to wav:', err);
      setIsUploading(false);
      setErrorNotification();
      return;
    }

    try {
      const { score } = await UserDictionaryApi.sendLearnablePronunciation({
        languagePairId,
        sessionId,
        learnableId: learnable.learnableIdKey,
        wavAudioBlob,
        retryCount,
        mode,
      });
      setAnalysisScore(score);
      if (score.result === 'pass') {
        onCorrectPronunciation?.();
      }
    } catch (err) {
      console.error('Could not send learnable pronunciation:', err);
      setErrorNotification();
    } finally {
      setIsUploading(false);
    }
  };

  const handleRecordingStop = async (audioBlob: Blob) => {
    setIsUploading(true);

    try {
      await onActivityEngagement?.();
    } catch {
      setIsUploading(false);
      setErrorNotification();
      return;
    }

    // defer WAV conversion since it will block the JS thread (will be fixed in WEBSITE-6974)
    setTimeout(() => void processRecording(audioBlob), 300);
  };

  const { isRecording, startRecording, stopRecording, lastRecording } = useAudioRecorder({
    maxDurationSec: MAX_RECORDING_DURATION_SEC,
    onStop: handleRecordingStop,
    onError: () => {},
  });

  useEffect(() => {
    if (immersive) {
      // The result card's "Try again" drives retries here
      return;
    }
    if (validationResult !== 'valid' || (analysisScore && analysisScore.result !== 'pass')) {
      setTimeout(() => {
        resetAnalysisScore();
        setValidationResult('valid');
        setRetryCount(c => c + 1);
      }, PRONUNCIATION_RESULT_DISPLAY_DURATION);
    }
  }, [immersive, analysisScore, validationResult, resetAnalysisScore]);

  if (!micPermissionState) {
    return null;
  }

  if (micPermissionState !== 'granted') {
    return (
      <MicPermissionsView
        isDenied={micPermissionState === 'denied'}
        onClick={handleGiveMicPermissions}
        compact={compact}
      />
    );
  }

  const handleRetry = () => {
    resetAnalysisScore();
    setValidationResult('valid');
    setRetryCount(c => c + 1);
  };

  if (immersive) {
    return (
      <>
        {debugPanelEnabled && (
          <PronunciationDebugPanel
            learnable={learnable}
            setScore={setAnalysisScore}
            onSkip={onSkip}
            onSendRecording={handleRecordingStop}
            lastScore={lastAnalysisScore}
            lastRecording={lastRecording}
            extraDebugInfo={extraDebugInfo}
          />
        )}
        <ImmersivePronunciationView
          learnable={learnable}
          progressIndicator={progressIndicator}
          sessionLearnables={sessionLearnables}
          isRecording={isRecording}
          startRecording={startRecording}
          stopRecording={stopRecording}
          isAnalysing={isUploading}
          score={analysisScore}
          validationResult={validationResult}
          onRetry={handleRetry}
          onContinue={onContinue}
        />
      </>
    );
  }

  if (isUploading || analysisScore || validationResult !== 'valid') {
    return (
      <PronunciationAnalysisView
        learnable={learnable}
        score={analysisScore}
        validationResult={validationResult}
        continueButtons={continueButtons}
        onError={() => {}}
        onContinue={onContinue}
        compact={compact}
      />
    );
  }

  return (
    <>
      {debugPanelEnabled && (
        <PronunciationDebugPanel
          learnable={learnable}
          setScore={setAnalysisScore}
          onSkip={onSkip}
          onSendRecording={handleRecordingStop}
          lastScore={lastAnalysisScore}
          lastRecording={lastRecording}
          extraDebugInfo={extraDebugInfo}
        />
      )}
      <PronunciationLearnableView
        learnable={learnable}
        progressIndicator={progressIndicator}
        isRecording={isRecording}
        startRecording={startRecording}
        stopRecording={stopRecording}
        onSkip={retryCount > 0 ? onSkip : undefined}
        compact={compact}
        hideVideoButton={hideVideoButton}
      />
    </>
  );
};
